'use client';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import ContactForm from './components/ContactForm';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/lib/translations';
import { CONTACT_EMAIL } from '@/lib/contact';

export default function ContactPage() {
  const { currentLang } = useLanguage();

  const contactInfo = [
    {
      id: 'contact_phone',
      icon: 'PhoneIcon',
      title: t('contact.phone', currentLang),
      value: '+386 31 301 086',
      href: 'tel:+38631301086',
    },
    {
      id: 'contact_email',
      icon: 'EnvelopeIcon',
      title: t('contact.email', currentLang),
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
    },
    {
      id: 'contact_location',
      icon: 'MapPinIcon',
      title: t('contact.location', currentLang),
      value: t('contact.address', currentLang),
      href: '#',
    },
  ];

  return (
    <>
      <Header currentLang={currentLang} />
      <main className="pt-40 pb-32 px-6 md:px-10 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4">
            {t('contact.title', currentLang)}
          </p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tighter mb-6">
            {t('contact.hero', currentLang)}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contact.description', currentLang)}
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {contactInfo.map((info) => (
            <a
              key={info.id}
              href={info.href}
              className="bg-card card-rounded p-6 text-center space-y-4 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Icon name={info.icon as any} size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">{info.title}</p>
                <p className="text-base font-semibold">{info.value}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Contact Form Section */}
        <section className="mt-32">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold tracking-tighter mb-8">
                {t('contact.form.title', currentLang)}
              </h2>
              <ContactForm currentLang={currentLang} />
            </div>

            {/* Info Section */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold tracking-tighter mb-4">
                  {t('contact.hours.title', currentLang)}
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <span className="font-medium text-foreground">
                      {t('contact.hours.weekday', currentLang)}
                    </span>{' '}
                    9:00 - 17:00
                  </p>
                  <p>
                    <span className="font-medium text-foreground">
                      {t('contact.hours.saturday', currentLang)}
                    </span>{' '}
                    9:00 - 13:00
                  </p>
                  <p>
                    <span className="font-medium text-foreground">
                      {t('contact.hours.sunday', currentLang)}
                    </span>{' '}
                    {t('contact.hours.closed', currentLang)}
                  </p>
                </div>
              </div>

              <div className="bg-accent/10 card-rounded p-6 space-y-3">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <Icon name="ClockIcon" size={24} className="text-accent-foreground" />
                </div>
                <h4 className="text-lg font-bold">{t('contact.response.title', currentLang)}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('contact.response.description', currentLang)}
                </p>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckCircleIcon" size={24} className="text-success" />
                </div>
                <h4 className="text-lg font-bold">{t('contact.personal.title', currentLang)}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('contact.personal.description', currentLang)}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Map or Additional CTA */}
        <section className="mt-32 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h3 className="text-3xl font-bold tracking-tighter">
              {t('contact.visit.title', currentLang)}
            </h3>
            <p className="text-lg text-muted-foreground">
              {t('contact.visit.description', currentLang)}
            </p>
            <div className="flex justify-center gap-4">
              <a href="#" className="btn-primary">
                {currentLang === 'sl' ? 'Naroči obisk' : 'Schedule Visit'}
              </a>
              <a href="/products" className="btn-secondary">
                {currentLang === 'sl' ? 'Oglej izdelke' : 'View Products'}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer currentLang={currentLang} />
    </>
  );
}
